/** ISO 4217 helpers. Amounts in the DB are integer minor units of `currency`. */

export const BASE_CURRENCY = 'GBP';

export const FX_SOURCES = [
  'identity',
  'hmrc_period',
  'ecb_frankfurter',
  'xero_total',
] as const;
export type FxSource = (typeof FX_SOURCES)[number];

export const COMMON_CURRENCIES = ['GBP', 'USD', 'EUR'] as const;

const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'VND', 'CLP']);

const SYMBOL: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
};

export function normalizeCurrency(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const t = raw.trim();
  if (!t) return null;
  if (t === '£') return 'GBP';
  if (t === '$') return 'USD';
  if (t === '€') return 'EUR';
  const code = t.toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return null;
  return code;
}

export function currencyExponent(code: string): number {
  return ZERO_DECIMAL.has(code.toUpperCase()) ? 0 : 2;
}

export function currencySymbol(code: string): string {
  return SYMBOL[code.toUpperCase()] ?? `${code.toUpperCase()} `;
}

/** GBP spend for display or recharge. Never the original foreign minor. */
export function sterlingNetPence(opts: {
  currency: string | null | undefined;
  net_pence: number | null | undefined;
  gbp_net_pence: number | null | undefined;
}): number | null {
  if (opts.gbp_net_pence != null) return opts.gbp_net_pence;
  const code = normalizeCurrency(opts.currency);
  if (code === BASE_CURRENCY && opts.net_pence != null) return opts.net_pence;
  return null;
}

/** GBP gross for display or Xero match. Never a foreign minor unit. */
export function sterlingGrossPence(opts: {
  currency: string | null | undefined;
  gross_pence: number | null | undefined;
  gbp_gross_pence: number | null | undefined;
}): number | null {
  if (opts.gbp_gross_pence != null) return opts.gbp_gross_pence;
  const code = normalizeCurrency(opts.currency);
  if (code === BASE_CURRENCY && opts.gross_pence != null) return opts.gross_pence;
  return null;
}

/**
 * List / confirmation amount. Sterling first when converted; original
 * kept as “issued” for a foreign row. Never prefixes £ on foreign gross.
 */
export function formatReceiptListAmount(opts: {
  currency: string | null | undefined;
  gross_pence: number | null | undefined;
  gbp_gross_pence: number | null | undefined;
}): string {
  const issued = formatMinor(opts.currency, opts.gross_pence);
  const sterling = sterlingGrossPence(opts);
  const code = normalizeCurrency(opts.currency);
  if (code && code !== BASE_CURRENCY) {
    if (sterling != null) {
      return `${formatMinor(BASE_CURRENCY, sterling)} · ${issued} issued`;
    }
    return issued;
  }
  if (sterling != null) return formatMinor(BASE_CURRENCY, sterling);
  return issued;
}

export function formatMinor(
  currency: string | null | undefined,
  minor: number | null | undefined,
): string {
  if (minor == null) return '—';
  const code = normalizeCurrency(currency);
  if (!code) {
    const n = minor / 100;
    return `${n.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} (currency unread)`;
  }
  const exp = currencyExponent(code);
  const n = minor / 10 ** exp;
  const body = n.toLocaleString('en-GB', {
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  });
  const sym = currencySymbol(code);
  return sym.endsWith(' ') ? `${sym}${body}` : `${sym}${body}`;
}

/** Sterling pence. Never assign an original-currency minor unit to this. */
export type GbpPence = number & { readonly __brand: 'GbpPence' };

export function asGbpPence(n: number): GbpPence {
  if (!Number.isInteger(n) || n < 0) {
    throw new Error('GBP pence must be a non-negative integer');
  }
  return n as GbpPence;
}

/**
 * Refuse identity-copy of a foreign amount into gbp_* columns.
 * GBP receipts must use rate 1 / identity; others must not.
 */
export function assertSterlingFx(opts: {
  currency: string | null | undefined;
  fx_source: FxSource | null | undefined;
  fx_rate: number | null | undefined;
  gross_pence: number | null | undefined;
  gbp_gross_pence: number | null | undefined;
}): void {
  const code = normalizeCurrency(opts.currency);
  if (!code) {
    if (opts.fx_source != null || opts.gbp_gross_pence != null) {
      throw new Error('GBP columns require a currency');
    }
    return;
  }
  if (code === BASE_CURRENCY) {
    if (opts.fx_source != null && opts.fx_source !== 'identity') {
      throw new Error('GBP receipts use the identity rate');
    }
    if (opts.fx_rate != null && Number(opts.fx_rate) !== 1) {
      throw new Error('GBP receipts must have fx_rate 1');
    }
    if (
      opts.gross_pence != null &&
      opts.gbp_gross_pence != null &&
      opts.gbp_gross_pence !== opts.gross_pence
    ) {
      throw new Error(
        'GBP columns must equal original amounts when currency is GBP',
      );
    }
    return;
  }
  if (opts.fx_source === 'identity') {
    throw new Error('Foreign amounts cannot be copied into GBP columns');
  }
  if (
    opts.gbp_gross_pence != null &&
    opts.gross_pence != null &&
    opts.gbp_gross_pence === opts.gross_pence &&
    (opts.fx_rate == null || Number(opts.fx_rate) === 1)
  ) {
    throw new Error('GBP column holds the original foreign amount');
  }
}

/**
 * Split a sterling gross in the same proportions as the issued-currency
 * line. Residual VAT so net + VAT always equals the sterling gross.
 */
export function apportionSterlingByIssuedSplit(opts: {
  gbp_gross_pence: number;
  net_pence: number;
  gross_pence: number;
}): { gbp_net_pence: number; gbp_vat_pence: number } {
  if (
    !Number.isInteger(opts.gbp_gross_pence) ||
    opts.gbp_gross_pence < 0 ||
    !Number.isInteger(opts.net_pence) ||
    opts.net_pence < 0 ||
    !Number.isInteger(opts.gross_pence) ||
    opts.gross_pence <= 0
  ) {
    throw new Error('Issued split and sterling gross must be non-negative integers');
  }
  const gbp_net_pence = Math.round(
    (opts.gbp_gross_pence * opts.net_pence) / opts.gross_pence,
  );
  return {
    gbp_net_pence,
    gbp_vat_pence: opts.gbp_gross_pence - gbp_net_pence,
  };
}

/** Convert original minor units to GBP pence using a major-unit FX rate. */
export function originalMinorToGbpPence(
  currency: string,
  minor: number,
  rate: number,
): GbpPence {
  const code = normalizeCurrency(currency) ?? BASE_CURRENCY;
  const origMajor = minor / 10 ** currencyExponent(code);
  return asGbpPence(Math.round(origMajor * rate * 100));
}

export function gbpFxColumns(opts: {
  currency: string | null;
  net_pence: number | null;
  vat_pence: number | null;
  gross_pence: number | null;
  rate: number;
  asOf: string;
  source: FxSource;
  period?: string | null;
}): {
  gbp_net_pence: GbpPence | null;
  gbp_vat_pence: GbpPence | null;
  gbp_gross_pence: GbpPence | null;
  fx_rate: number;
  fx_rate_as_of: string;
  fx_source: FxSource;
  fx_period: string | null;
} {
  const code = normalizeCurrency(opts.currency) ?? BASE_CURRENCY;
  const conv = (n: number | null) =>
    n == null ? null : originalMinorToGbpPence(code, n, opts.rate);
  const out = {
    gbp_net_pence: conv(opts.net_pence),
    gbp_vat_pence: conv(opts.vat_pence),
    gbp_gross_pence: conv(opts.gross_pence),
    fx_rate: opts.rate,
    fx_rate_as_of: opts.asOf,
    fx_source: opts.source,
    fx_period: opts.source === 'hmrc_period' ? (opts.period ?? null) : null,
  };
  if (out.fx_source === 'hmrc_period' && !out.fx_period) {
    throw new Error('HMRC period rate requires fx_period YYYY-MM');
  }
  assertSterlingFx({
    currency: code,
    fx_source: out.fx_source,
    fx_rate: out.fx_rate,
    gross_pence: opts.gross_pence,
    gbp_gross_pence: out.gbp_gross_pence,
  });
  return out;
}
